import { useState, useMemo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";

// material-ui
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  ManageAccounts as ManageAccountsIcon,
  CalendarMonth as CalendarMonthIcon,
  WebStories as WebStoriesIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  SoupKitchen as SoupKitchenIcon,
  Logout as LogoutIcon,
  WbSunnyRounded as WbSunnyRoundedIcon,
  ModeNightRounded as ModeNightRoundedIcon,
  EventRepeat as EventRepeatIcon,
  Insights as InsightsIcon,
} from "@mui/icons-material";

// assets
import { IconLogout } from "@tabler/icons-react";
import { useThemeContext } from "themes/CustomThemeProvider";

const MobileNavigation = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useSupabaseAuth();
  const preferences = useSelector(
    (state) => state.preferenceReducer.preferences
  );
  const colorMode = useThemeContext();

  // Determine avatar URL - prioritize preferences.avatar_url over user_metadata
  const avatarUrl = useMemo(() => {
    return (
      preferences?.avatar_url ||
      currentUser?.user_metadata?.avatar_url ||
      currentUser?.user_metadata?.picture ||
      ""
    );
  }, [
    preferences?.avatar_url,
    currentUser?.user_metadata?.avatar_url,
    currentUser?.user_metadata?.picture,
  ]);

  const handleLogout = async () => {
    try {
      setDrawerOpen(false);
      const { error } = await logout();
      if (error) {
        console.error("Logout error:", error);
      } else {
        navigate("/sign-in");
      }
    } catch (error) {
      console.error("Unexpected error during logout:", error);
      // Still navigate even if there's an error
      navigate("/sign-in");
    }
  };

  const handleNavigation = (route) => {
    navigate(route);
    setDrawerOpen(false);
  };

  const navigationItems = [
    { text: "Dashboard", icon: <DashboardIcon />, route: "/dashboard" },
    { text: "Clients", icon: <PeopleIcon />, route: "/clients" },
    { text: "Engagements", icon: <ReceiptIcon />, route: "/engagements" },
    { text: "Insights", icon: <InsightsIcon />, route: "/insights" },
    { text: "Network", icon: <PeopleIcon />, route: "/network" },
  ];

  const profileItems = [
    {
      text: "Account Settings",
      icon: <CurrencyExchangeIcon />,
      route: "/settings",
    },
    {
      text: "Financial Settings & Goals",
      icon: <CurrencyExchangeIcon />,
      route: "/financial-settings",
    },
    { text: "Calendars", icon: <CalendarMonthIcon />, route: "/calendars" },
    {
      text: "History & Activities",
      icon: <WebStoriesIcon />,
      route: "/history",
    },
    {
      text: "Invoice Settings",
      icon: <CurrencyExchangeIcon />,
      route: "/invoice-settings",
    },
    {
      text: "My Subscription",
      icon: <EventRepeatIcon />,
      route: "/subscription",
    },
    // { text: "Get Help!", icon: <SoupKitchenIcon />, route: "/help" },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <IconButton
        onClick={() => setDrawerOpen(true)}
        sx={{
          display: { xs: "flex", md: "none" },
          color: "inherit",
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* User Profile Section */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, p: 1 }}>
            <Avatar src={avatarUrl} sx={{ mr: 2, width: 48, height: 48 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {currentUser?.displayName || "User"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentUser?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Main Navigation */}
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ px: 2, mb: 1 }}
          >
            Navigation
          </Typography>
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Link
                  to={item.route}
                  onClick={() => setDrawerOpen(false)}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <ListItemButton
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      backgroundColor:
                        location.pathname === item.route
                          ? "action.hover"
                          : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </Link>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Profile Navigation */}
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ px: 2, mb: 1 }}
          >
            Account
          </Typography>
          <List>
            {profileItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Link
                  to={item.route}
                  onClick={() => setDrawerOpen(false)}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <ListItemButton
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      backgroundColor:
                        location.pathname === item.route
                          ? "action.hover"
                          : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </Link>
              </ListItem>
            ))}

            {/* Dark Mode Toggle */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={colorMode.toggleColorMode}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {colorMode.mode === "dark" ? (
                    <WbSunnyRoundedIcon />
                  ) : (
                    <ModeNightRoundedIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    colorMode.mode === "dark" ? "Light Mode" : "Dark Mode"
                  }
                />
              </ListItemButton>
            </ListItem>

            {/* Logout Button */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  color: "error.main",
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                  <IconLogout stroke={1.5} size="1.3rem" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNavigation;
